import { memo, useContext, useRef, useState } from 'react';
import { useFieldArray } from 'react-hook-form';

import { useLanguage } from '@languages/index';
import CampaignFormField from '../../../../../../../campaignFormFieldGroup/components/campaignFormField/campaignFormField';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import useUploadGuidelineImage from 'hooks/useUploadGuidelineImage/useUploadGuidelineImage';
import Checkbox from 'components/checkbox';
import { GUIDELINE_RULE_DTO_ENUMS } from 'api/repositories/guidelineRules/guidelineRules.constants';

const EmblemField = (props: {
  label: string;
  emblemGroupName: typeof GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.EMBLEM_OLIVEYOUNG;
  emblemUrls: string[];
}) => {
  const CAN_UPLOAD = false;
  const CAN_DELETE = false;

  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };

  const { guidelineRuleFormMethods, modal } = useContext(CampaignFormContext);

  const emblem = useFieldArray({
    control: guidelineRuleFormMethods.control,
    name: props.emblemGroupName,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    mutate: uploadGuideImageMutate,
    isPending: isUploadingEmblemImage,
  } = useUploadGuidelineImage();

  const onClickUploadEmblemImageButton = () => {
    if (!isUploadingEmblemImage) {
      fileInputRef.current.value = '';
      fileInputRef.current?.click();
    }
  };

  const onChangeEmblemImage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const image = event.target.files?.[0];

    if (image) {
      uploadGuideImageMutate(image, {
        onSuccess: (data) => {
          emblem.append({
            value: JSON.stringify({
              url: data.data.url,
              description: '',
            }),
          });
        },
        onError: (error: Error) => {
          modal.showModal({
            text: error?.message,
          });
        },
      });
    }
  };

  const onDeleteEmblem = (index: number) => {
    modal.showModal({
      text: languageTexts.create_delete_image_confirm,
      confirm: () => {
        emblem.remove(index);
      },
    });
  };

  return (
    <CampaignFormField
      as="div"
      wrapperClassName="!mt-[28px]"
      label={
        <>
          <span className="text-[17px]">
            {props.label}
          </span>
          <span className="px-[4px] py-[2px] text-white bg-black text-[12px] font-bold select-none leading-none">
            {languageTexts.recommendation}
          </span>
        </>
      }
      labelClassName="flex flex-row items-center gap-[6px]"
    >
      <Checkbox
        text={languageTexts.create_reels_form_emblem_guideline_check}
        checked={emblem.fields.length > 0}
        onClick={() => {
          if (emblem.fields.length > 0) {
            emblem.remove();
          } else {
            props.emblemUrls.forEach((url) => {
              emblem.append({
                value: JSON.stringify({
                  url,
                  description: '',
                }),
              });
            });
          }
        }}
      />

      {emblem.fields.length > 0 && (
        <p className={`text-sm text-[#999]`}>
          {languageTexts.create_reels_form_emblem_guideline_desc}
        </p>
      )}

      {/** 엠블럼 이미지 영역 */}
      <div className="flex flex-col gap-[10px]">
        {emblem.fields.map((field, index) => {
          const { url } = JSON.parse(field.value as string);

          return (
            <div key={`brand-logo-${index}`} className="relative flex flex-col">
              <div className="relative flex w-full max-h-[200px] border border-[#E8E8E8] bg-[#888888] p-[40px]">
                <img className="object-contain" src={url} />

                {/* 엠블럼 삭제 버튼 */}
                {CAN_DELETE && (
                  <div
                    className="absolute top-0 right-0 cursor-pointer bg-black"
                    onClick={() => onDeleteEmblem(index)}
                  >
                    <img className="p-1" src={'/assets/icons/close-white.svg'} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 엠블럼 업로드 버튼 */}
      {(CAN_UPLOAD && emblem.fields.length < 2) && (
        <button
          type="button"
          className="flex justify-center items-center cursor-pointer mx-auto py-3 w-full border-[1px] border-[#E8E8E8]"
          onClick={onClickUploadEmblemImageButton}
        >
          {languageTexts.create_reels_form_emblem_guideline_upload}
          <input
            type="file"
            className="hidden"
            ref={fileInputRef}
            onChange={onChangeEmblemImage}
          />
        </button>
      )}
    </CampaignFormField>
  );
};

export default memo(EmblemField);
